import { useEffect } from "react";

const SEO = ({
  title,
  description = "Offensive security portfolio documenting CTF writeups, penetration testing research, and red team techniques by Maxwell Ferreira.",
  image = null,
  type = "website",
  canonical = null,
  robots = "index,follow",
  jsonLd = null
}) => {
  // Stable string so the effect only re-runs when the structured data changes.
  const jsonLdString = jsonLd ? JSON.stringify(jsonLd) : null;
  const envSiteUrl = process.env.REACT_APP_SITE_URL?.replace(/\/+$/, "") || "";
  const siteUrl = typeof window !== "undefined"
    ? envSiteUrl || window.location.origin
    : envSiteUrl;
  const fullUrl = typeof window !== "undefined" ? window.location.href : siteUrl;
  const canonicalUrl = canonical
    ? (canonical.startsWith("http") ? canonical : `${siteUrl}${canonical}`)
    : fullUrl;
  const pageTitle = title ? String(title) : 'ZeroDay.log';
  const displayTitle = title && title !== 'ZeroDay.log' ? `${pageTitle} | ZeroDay.log` : 'ZeroDay.log';
  const imageUrl = image ? (image.startsWith('http') ? image : `${siteUrl}${image}`) : null;
  
  useEffect(() => {
    // Set document title
    document.title = displayTitle;
    
    // Update or create meta tags
    const updateMeta = (name, content, property = false) => {
      const attr = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    
    updateMeta('description', String(description));
    updateMeta('robots', String(robots));
    updateMeta('og:type', type, true);
    updateMeta('og:url', canonicalUrl, true);
    updateMeta('og:title', displayTitle, true);
    updateMeta('og:description', String(description), true);
    updateMeta('og:site_name', 'ZeroDay.log', true);
    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:title', displayTitle);
    updateMeta('twitter:description', String(description));
    
    if (imageUrl) {
      updateMeta('og:image', imageUrl, true);
      updateMeta('twitter:image', imageUrl);
    }
    
    // Update canonical link
    let canonicalEl = document.querySelector('link[rel="canonical"]');
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute('href', canonicalUrl);

    // Structured data (JSON-LD). One managed <script> we keep in sync.
    let ld = document.getElementById('seo-jsonld');
    if (jsonLdString) {
      if (!ld) {
        ld = document.createElement('script');
        ld.type = 'application/ld+json';
        ld.id = 'seo-jsonld';
        document.head.appendChild(ld);
      }
      ld.textContent = jsonLdString;
    } else if (ld) {
      ld.remove();
    }
  }, [canonicalUrl, displayTitle, description, imageUrl, robots, type, jsonLdString]);
  
  return null;
};

export default SEO;
